import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Static } from "../../interfaces/Static";
import { getDropdownsByID } from "../../utils/Statics";
import { StaticTypes } from "../../enums/StaticTypes";
import Heading from "../../components/Heading";
import FormHeading from "../../components/FormHeading";
import CustomSelect from "../../components/CustomSelect";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  nightParkingState,
  residentialAddressState,
} from "../../state/QuoteState";
import { ParkingType } from "../../interfaces/Parking";
import { Parking } from "../../enums/Parking";
import Address from "./Address";
import CustomDivider from "../../components/CustomDivider";
import { isRoaming } from "../../utils/Common";

const NightParking = ({
  setIsFormValid,
}: {
  setIsFormValid: (value: boolean) => void;
}) => {
  const { data: statics } = useQuery<Static[]>({ queryKey: ["statics"] });
  const [nightParking, setNightParking] = useRecoilState(nightParkingState);
  const residentialAddress = useRecoilValue(residentialAddressState);
  const [isAddressValid, setIsAddressValid] = useState(false);

  const { parkingLocation, parkingMethod } = nightParking;

  const nightParkingLocations = useMemo(() => {
    return getDropdownsByID(
      StaticTypes.VEHICLE_PARKING_LOCATIONS,
      statics || []
    );
  }, [statics]);

  const nightParkingMethods = useMemo(() => {
    const dropdowns = getDropdownsByID(
      StaticTypes.VEHICLE_PARKING_METHODS,
      statics || []
    );

    const indexOfObject = dropdowns.findIndex((dd) => {
      return dd.code === "10";
    });

    if (indexOfObject !== -1) {
      dropdowns.splice(indexOfObject, 1);
    }

    return dropdowns;
  }, [statics]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (parkingLocation === Parking.SAME) {
      setNightParking((prev) => ({
        ...prev,
        ...residentialAddress,
      }));
    } else if (isRoaming(parkingLocation)) {
      setNightParking({
        ...nightParking,
        address1: "",
        address2: "",
        address3: "",
        parkingMethod: "",
        postalCode: "",
        suburb: "",
      });
    }
  }, [parkingLocation]);

  useEffect(() => {
    if (isRoaming(parkingLocation)) {
      setIsFormValid(true);
    } else if (parkingLocation === Parking.SAME) {
      setIsFormValid(parkingMethod !== "");
    } else if (parkingLocation === Parking.DIFFERENT) {
      setIsFormValid(isAddressValid && parkingMethod !== "");
    } else {
      setIsFormValid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingLocation, parkingMethod, isAddressValid]);

  const handleInputChange = (
    value: string | boolean,
    field: keyof ParkingType
  ) => {
    setNightParking((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Heading>Night parking</Heading>
      <FormHeading>Parking Location</FormHeading>
      <CustomSelect
        items={nightParkingLocations}
        value={parkingLocation}
        placeholder="Night parking Location"
        onSelect={(value: string) => {
          handleInputChange(value, "parkingLocation");
        }}
      />
      {(parkingLocation === Parking.SAME ||
        parkingLocation === Parking.DIFFERENT) && (
        <>
          <CustomDivider />

          <FormHeading>Night Parking Details</FormHeading>
          <CustomSelect
            items={nightParkingMethods}
            value={parkingMethod}
            placeholder="Night parking Location"
            onSelect={(value: string) => {
              handleInputChange(value, "parkingMethod");
            }}
          />
        </>
      )}
      {parkingLocation === Parking.DIFFERENT && (
        <>
          <CustomDivider />

          <Address
            data={nightParking}
            changeValue={handleInputChange}
            setIsAddressValid={setIsAddressValid}
          />
        </>
      )}
    </>
  );
};

export default NightParking;
